import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Checkbox, FormControlLabel, Box, CircularProgress, Autocomplete, FormControl, InputLabel, MenuItem, TextField, Paper, Select, Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import UserService from "services/UserService";
import { useAuth } from "context/authContext";
import ImagePicker from 'components/MDImagePicker';
import CreateUserDialog from './components/CreateUserDialog';
import SupplierService from 'services/SupplierService'
import TennantService from 'services/TennantService'
import SearchAutocomplete from 'components/SearchAutocomplete'

export default function TabTennant() {
    const navigate = useNavigate();
    const { token, user, isAuthenticated } = useAuth();
    const [isEdit, setIsEdit] = useState(false);
    const [reload, setReload] = useState(false);
    const [users, setUsers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [tennants, setTennants] = useState([]);
    const [updatedUser, setUpdatedUser] = useState({});
    const [userCreate, setUserCreate] = useState({});
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [alert, setAlert] = useState({ type: '', message: '' });
    const [openDialog, setOpenDialog] = useState(false);
    const [searchSupplierLoading, setSearchSupplierLoading] = useState(false);
    const [searchTennantLoading, setSearchTennantLoading] = useState(false);
    const [searchUserLoading, setSearchUserLoading] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [selectedTennant, setSelectedTennant] = useState(null);
    const [selectedSupplierUserCreate, setSelectedSupplierUserCreate] = useState(null);
    const [selectedTennantUserCreate, setSelectedTennantUserCreate] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);


    useEffect(() => {
        try {
            setLoading(true);
            const init = async () => {
                const authenticated = await isAuthenticated();
                if (!authenticated) {
                    navigate(`/login`);
                    return;
                }

                await loadData();
            };

            init();
        } catch (error) {
            console.error("Erro ao carregar Tab Tennant:", error);
            navigate(`/login`);
        }
    }, [navigate, user, reload]);

    const payload = (listUserFromAPI) =>
        listUserFromAPI.map(({
            RefreshToken,
            PrivacyConsentAcceptedAt,
            PrivacyConsentVersion,
            PrivacyConsentAcceptedFromIp,
            ...user
        }) => user);

    const loadData = async () => {
        try {
            const [
                usersResponse,
                supplierResponse,
                tennantResponse,
            ] = await Promise.all([
                await UserService.getUsers(token, { page: 1, pageSize: 150 }),
                await SupplierService.GetSuppliers(token, { page: 1, pageSize: 150 }),
                await TennantService.GetTennants(token, null, { page: 1, pageSize: 150 })
            ]);
            setSuppliers(supplierResponse.data.data);
            setTennants(tennantResponse.data);

            const userList = payload(usersResponse.data.data);
            const userDefault = userList[0] ?? {};

            setUsers(userList);
            setUpdatedUser(userDefault);
            setSelectedSupplier(supplierResponse.data.data?.find(supplier => String(supplier.Id) === String(userDefault?.SupplierId)) || null);
            setSelectedTennant(tennantResponse.data?.find(tennant => String(tennant.Id) === String(userDefault?.TennantId)) || null);
            setSelectedUser(userDefault);
        } catch (error) {
            console.error('Erro ao carregar users', error);
            console.log('Status:', error.response?.status);
            console.log('Resposta da API:', error.response?.data);
            console.log('Request:', error.config?.data);
        } finally {
            setLoading(false);
        }
    };

    const onSupplierSearch = async (value) => {
        try {
            setSearchSupplierLoading(true);
            const response = await SupplierService.GetSuppliers(token, { page: 1, pageSize: 20, find: value === '' ? null : value });
            setSuppliers(response.data.data);
        } catch (error) {
            console.error(error);
            setSuppliers([]);
        } finally {
            setSearchSupplierLoading(false);
        }
    };

    const onUserSearch = async (value) => {
        try {
            setSearchUserLoading(true);
            const response = await UserService.getUsers(token, { page: 1, pageSize: 20, find: value === '' ? null : value });
            setUsers(response.data.data);
        } catch (error) {
            console.error(error);
            setUsers([]);
        } finally {
            setSearchUserLoading(false);
        }
    };

    const onTennantSearch = async (value) => {
        try {
            setSearchTennantLoading(true);
            const response = await TennantService.GetTennants(token, null, { page: 1, pageSize: 20, find: value === '' ? null : value });
            setTennants(response.data);
        } catch (error) {
            console.error(error);
            setTennants([]);
        } finally {
            setSearchTennantLoading(false);
        }
    };

    const clearStates = () => {
        setUpdatedUser({});
    }

    const onClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setOpen(false);
    };

    const handleSelectChange = (value) => {
        const selectedUser = users.find((t) => t.Id === value);
        setUpdatedUser(selectedUser);
        setSelectedSupplier(suppliers?.find(x => x.Id === selectedUser?.SupplierId) || null);
        setSelectedTennant(tennants?.find(x => x.Id === selectedUser?.TennantId) || null);
        setIsEdit(false);
    }

    const onChange = (name, value) => {
        if (name === 'RoleId') {
            setUpdatedUser((prev) => ({
                ...prev,
                Role: {
                    ...prev.Role,
                    Id: value,
                },
            }));
        } else {
            setUpdatedUser((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
        setIsEdit(true);
    };

    const handleFillUserCreate = (name, value) => {
        if (name === 'RoleId') {
            setUserCreate((prev) => ({
                ...prev,
                Role: {
                    ...prev.Role,
                    Id: value,
                },
            }));
        } else {
            setUserCreate((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const onCancel = () => {
        handleSelectChange(updatedUser.Id);
    }

    const onDelete = async () => {
        try {
            await UserService.deleteUser(updatedUser.Id, token);
            setAlert({ type: 'success', message: 'Usuário excluído com sucesso.' });
            setOpen(true);
            clearStates();
        } catch (error) {
            setAlert({ type: 'error', message: `Erro ao excluir usuário ${error.response?.data?.error[0]}` });
            setOpen(true);
            console.error('Erro ao excluir tennant', error);
            console.log('Status:', error.response?.status);
            console.log('Resposta da API:', error.response?.data);
            console.log('Request:', error.config?.data);
        } finally {
            setReload(!reload);
        }
    }

    const onSave = async () => {
        try {
            const { Id, createdAt, updatedAt, ...savingData } = updatedUser;
            await UserService.editUser(Id, payloadUser(savingData));
            setAlert({ type: 'success', message: 'Dados salvos com sucesso.' });
            setOpen(true);
            setIsEdit(false);
            setReload(!reload);
        } catch (error) {
            setAlert({ type: 'error', message: `Erro ao salvar dados ${error.response?.data?.error[0]}` });
            setOpen(true);
            console.log('Status:', error.response?.status);
            console.log('Resposta da API:', error.response?.data);
            console.log('Request:', error.config?.data);
        }
    }

    const onPasswordReset = async () => {
        try {
            await UserService.resetPassword({ userEmail: updatedUser.Email });
            setAlert({ type: 'success', message: 'Redefinição de senha enviada para o e-mail do usuário.' });
            setOpen(true);
        }
        catch (error) {
            setAlert({ type: 'error', message: `Erro ao salvar dados ${error.response?.data?.error[0]}` });
            setOpen(true);
            console.log('Status:', error.response?.status);
            console.log('Resposta da API:', error.response?.data);
            console.log('Request:', error.config?.data);
        }
    }

    const onCreateCancel = () => {
        setOpenDialog(false);
        setUserCreate({});
        setSelectedSupplierUserCreate(null);
        setSelectedTennantUserCreate(null);
    }

    const payloadUser = (user) => {
        const payload = {
            Name: user?.Name,
            Email: user?.Email,
            SupplierId: user?.SupplierId,
            TennantId: user?.TennantId,
            RoleId: user?.Role?.Id,
            Main: Boolean(user?.Main),
        };
        delete payload.Role
        return payload
    }

    const onCreateUser = async () => {
        try {
            await UserService.postUser(payloadUser(userCreate), token);
            setAlert({ type: 'success', message: 'Usuário criado com sucesso!' });
            setOpen(true);
            setIsEdit(false);
            setReload(!reload);
            onCreateCancel();
        } catch (error) {
            setAlert({ type: 'error', message: `Erro ao criar usuário ${error.response?.data?.error[0]}` });
            setOpen(true);
            console.log('Status:', error.response?.status);
            console.log('Resposta da API:', error.response?.data);
            console.log('Request:', error.config?.data);
        }
    }

    const onSelectedValueFromCreateUser = (value, inputName) => {
        if (String(inputName).toUpperCase() === 'TENNANT') {
            setSelectedTennantUserCreate(value);
        } else if (String(inputName).toUpperCase() === 'SUPPLIER') {
            setSelectedSupplierUserCreate(value);
        }
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress />
            </Box>
        );
    }

    const snackBar = (
        <Snackbar open={open} autoHideDuration={3000} onClose={onClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
            <MuiAlert onClose={onClose} severity={alert.type} sx={{ width: "100%" }}>
                {alert.message}
            </MuiAlert>
        </Snackbar>
    )

    const permissoesOptions = [
        { Value: 1, Label: "Admin" },
        { Value: 2, Label: "Usuário" },
        { Value: 3, Label: "Integrador" },
        { Value: 4, Label: "UnloggedUser" },
    ];

    const modalCreateUser = (
        <CreateUserDialog
            open={openDialog}
            user={userCreate}
            onChange={handleFillUserCreate}
            onCancel={onCreateCancel}
            onConfirm={onCreateUser}
            onSelectedValue={onSelectedValueFromCreateUser}
            suppliers={suppliers}
            tennants={tennants}
            selectedSupplier={selectedSupplierUserCreate}
            selectedTennant={selectedTennantUserCreate}
            permissoesOptions={permissoesOptions}
            onSupplierSearch={onSupplierSearch}
            onTennantSearch={onTennantSearch}
            searchSupplierLoading={searchSupplierLoading}
            searchTennantLoading={searchTennantLoading}
        />
    )

    return (
        <MDBox mt={2}>
            <Paper
                elevation={3}
                sx={{
                    backgroundColor: '#fff',
                    padding: 2,
                    borderRadius: 3,
                    width: '100%',
                }}
            >
                <Box
                    mt={2}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        width: "100%",
                    }}
                >
                    <SearchAutocomplete
                        label="Selecione"
                        options={users}
                        loading={searchUserLoading}
                        value={selectedUser}
                        getOptionLabel={(user) =>
                            user
                                ? `${user.Id} - ${user.Name}`
                                : ''
                        }
                        onSearch={onUserSearch}
                        onChange={(user) => {
                            setSelectedUser(user)
                            handleSelectChange(user?.Id ?? '');
                        }}
                    />
                    <MDButton variant="contained" color="success" size="medium" onClick={() => setOpenDialog(true)}>
                        Criar
                    </MDButton>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        width: "100%",
                        mt: 4,
                        mb: 2
                    }}
                >
                    <TextField
                        fullWidth
                        label="Nome"
                        type="text"
                        variant="outlined"
                        value={updatedUser?.Name || ''}
                        onChange={(e) => onChange('Name', e.target.value)}
                    />

                    <TextField
                        fullWidth
                        label="E-mail"
                        type="email"
                        variant="outlined"
                        value={updatedUser?.Email || ''}
                        onChange={(e) => onChange('Email', e.target.value)}
                    />
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        width: "100%",
                        mb: 2
                    }}
                >
                    <SearchAutocomplete
                        label="Empresa"
                        options={tennants}
                        loading={searchTennantLoading}
                        value={selectedTennant}
                        getOptionLabel={(tennant) =>
                            tennant
                                ? `${tennant.Id} - ${tennant.CompanyName}`
                                : ''
                        }
                        onSearch={onTennantSearch}
                        onChange={(tennant) => {
                            setSelectedTennant(tennant)
                            onChange(
                                'TennantId',
                                tennant ? tennant.Id : null
                            );
                        }}
                    />

                    <SearchAutocomplete
                        label="Fornecedor"
                        options={suppliers}
                        loading={searchSupplierLoading}
                        value={selectedSupplier}
                        getOptionLabel={(supplier) =>
                            supplier
                                ? `${supplier.Id} - ${supplier.CompanyName}`
                                : ''
                        }
                        onSearch={onSupplierSearch}
                        onChange={(supplier) => {
                            setSelectedSupplier(supplier)
                            onChange(
                                'SupplierId',
                                supplier ? supplier.Id : null
                            );
                        }}
                    />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        width: "100%",
                        mb: 2
                    }}
                >
                    <FormControl fullWidth variant="outlined" sx={{ minWidth: 200 }}>
                        <InputLabel id="permission-select-label">
                            Permissão
                        </InputLabel>

                        <Select
                            labelId="permission-select-label"
                            id="permission-select"
                            value={updatedUser?.Role?.Id ?? ''}
                            label="Permissão"
                            onChange={(e) => { onChange('RoleId', e.target.value) }}
                            sx={{
                                height: "45px"
                            }}
                        >
                            {permissoesOptions.map((option) => (
                                <MenuItem
                                    key={option.Value}
                                    value={option.Value}
                                >
                                    {option.Label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControlLabel
                        label="Principal?"
                        labelPlacement='start'
                        sx={{ width: "30%" }}
                        control={
                            <Checkbox
                                checked={updatedUser?.Main ?? false}
                                onChange={(e) => onChange('Main', (e?.target?.checked ?? false))}
                            />
                        } />
                </Box>
                <Box
                    mt={2}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        width: "100%",
                    }}
                >
                    <MDButton variant="contained" color="success" size="medium" disabled={!isEdit} onClick={onSave}>
                        Salvar
                    </MDButton>
                    <MDButton variant="contained" color="info" size="medium" disabled={!isEdit} onClick={onCancel}>
                        Cancelar
                    </MDButton>
                    <MDButton variant="contained" color="warning" size="medium" disabled={updatedUser?.Id === undefined} onClick={onPasswordReset}>
                        Resetar senha
                    </MDButton>
                    <MDButton variant="contained" color="error" size="medium" disabled={updatedUser?.Id === undefined} onClick={onDelete}>
                        Excluir
                    </MDButton>
                </Box>
                {snackBar}
                {modalCreateUser}
            </Paper>
        </MDBox>
    )
}